import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiError, safeNext } from "@/lib/api";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("beginner");

  const [neutral, setNeutral] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await signup(email.trim(), password, name.trim(), mode);
      localStorage.setItem("apx:ui-mode", mode);
      if (u) {
        toast.success("Account created · $100,000 virtual balance ready · check your inbox to confirm your email");
        nav(next);
      } else {
        setNeutral(true);  // address may already have an account — same message either way
      }
    } catch (err) {
      toast.error(apiError(err, "Signup failed"));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 grain">
      <div className="w-full max-w-md apx-glass rounded-2xl p-8 rise" data-testid="signup-card">
        <div className="flex flex-col items-center mb-8">
          <img
            data-testid="brand-hero-logo"
            src="/brand/evidex-logo-notag.jpg"
            alt="EV-IDEX"
            className="w-full max-w-[340px] h-auto rounded-xl"
          />
          <div className="mt-3 text-[10px] font-mono-ticker uppercase tracking-widest text-slate-500">
            Crypto Research · Paper Trading
          </div>
        </div>

        <h1 className="font-display text-3xl font-bold mb-1">Create account</h1>
        <p className="text-sm text-slate-400 mb-6">Start with a $100,000 virtual trading balance.</p>

        {neutral && (
          <div className="apx-card p-4 mb-5 text-sm text-slate-200 border border-cyan-800/60" data-testid="signup-neutral-notice">
            <div className="font-semibold text-cyan-300">Check your inbox</div>
            <p className="text-xs text-slate-400 mt-1">We've sent an email to <span className="font-mono-ticker">{email}</span>. If this address already had an EV-IDEX account, the email explains how to sign in instead — or use <Link to="/forgot-password" className="text-cyan-300 hover:underline" data-testid="signup-neutral-forgot">Forgot your password?</Link></p>
          </div>
        )}
        <form onSubmit={submit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-mono-ticker uppercase tracking-widest text-slate-500">Name</span>
            <input
              data-testid="signup-name"
              required value={name}
              onChange={(e) => setName(e.target.value)}
              className="apx-ring px-3 py-2.5 rounded-lg bg-slate-900/80 border border-slate-800 outline-none"
              placeholder="Alex"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-mono-ticker uppercase tracking-widest text-slate-500">Email</span>
            <input
              data-testid="signup-email"
              required type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="apx-ring px-3 py-2.5 rounded-lg bg-slate-900/80 border border-slate-800 outline-none"
              placeholder="you@ev-idex.com"
            />
          </label>
          <label className="flex flex-col gap-1.5">
            <span className="text-xs font-mono-ticker uppercase tracking-widest text-slate-500">Password</span>
            <input
              data-testid="signup-password"
              required type="password" minLength={8} value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="apx-ring px-3 py-2.5 rounded-lg bg-slate-900/80 border border-slate-800 outline-none"
              placeholder="At least 6 characters"
            />
          </label>
          <fieldset className="flex flex-col gap-1.5" data-testid="signup-mode">
            <legend className="text-xs font-mono-ticker uppercase tracking-widest text-slate-500 mb-1.5">Your crypto experience</legend>
            <div className="grid grid-cols-2 gap-2">
              {[["beginner", "New to crypto", "Plain-English explanations, examples and guidance on every page."], ["advanced", "Experienced", "All indicators and analytics shown at once."]].map(([v, l, d]) => (
                <button type="button" key={v} data-testid={`signup-mode-${v}`} onClick={() => setMode(v)} aria-pressed={mode === v}
                        className={`text-left rounded-lg border p-3 transition-colors ${mode === v ? "border-cyan-700 bg-cyan-500/10" : "border-slate-800 bg-slate-900/60 hover:border-slate-700"}`}>
                  <div className="text-sm font-medium">{l}</div><div className="text-[11px] text-slate-400 mt-0.5">{d}</div>
                </button>
              ))}
            </div>
            <div className="text-[10px] text-slate-500">You can switch modes any time in Settings — nothing is hidden for good.</div>
          </fieldset>
          <button
            type="submit"
            data-testid="signup-submit"
            disabled={busy}
            className="btn-primary rounded-lg py-2.5 font-medium flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Create account
          </button>
        </form>

        <div className="mt-6 text-sm text-slate-400">
          Already have an account?{" "}
          <Link data-testid="go-login" to="/login" className="text-cyan-300 hover:underline">Sign in</Link>
        </div>
        <div className="mt-6 text-[11px] text-slate-500 leading-relaxed" data-testid="auth-legal">
          By continuing you agree to the <Link to="/terms" className="text-slate-300 hover:text-cyan-200 underline">Terms of Service</Link>, <Link to="/privacy" className="text-slate-300 hover:text-cyan-200 underline">Privacy Policy</Link> and <Link to="/risk-disclosure" className="text-slate-300 hover:text-cyan-200 underline">Risk Disclosure</Link>. EV-IDEX is a research and paper-trading platform — not a broker, exchange or adviser, and never a guarantee of returns.
        </div>
        <div className="mt-3 text-[11px] text-slate-500 flex flex-wrap gap-x-3"><Link to="/" className="hover:text-cyan-200">About EV-IDEX</Link><Link to="/contact" className="hover:text-cyan-200">Contact & Support</Link><Link to="/partners" className="hover:text-cyan-200">Investment Partners</Link></div>
      </div>
    </div>
  );
}
